import { FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { AudienceSummary } from '@/api/types';
import { AUDIENCE_STATUS_LABELS, formatAudienceReplyRatePct } from '@marketing-hub/shared';
import { useProjectContext } from '@/project/ProjectContext';
import { ProjectGate } from '@/features/projects/ProjectGate';
import { ProjectPickerEmpty } from '@/features/projects/ProjectPickerEmpty';
import { ProjectWorkspaceHeader } from '@/features/projects/ProjectWorkspaceHeader';
import { AudiencesFlagGate } from '@/features/audiences/AudiencesFlagGate';
import { Button } from '@/components/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/components/ui/Field';
import { ErrorState, LoadingState } from '@/components/StateMessage/StateMessage';
import styles from './DoelgroepenPage.module.css';

const TYPE_LABELS: Record<string, string> = {
  static: 'Statisch',
  dynamic: 'Dynamisch',
};

type SortKey = 'updated' | 'name' | 'contacts' | 'opportunity' | 'campaigns';

function statusBadgeClass(status: string): string {
  if (status === 'active') return styles.badge_active;
  if (status === 'archived') return styles.badge_archived;
  return styles.badge_draft;
}

function displayContactCount(a: AudienceSummary): number {
  if (!a.parent_audience_id && (a.children_count ?? 0) > 0) {
    return a.rollup_lead_count ?? a.lead_count;
  }
  return a.lead_count;
}

function ScoreMini({ value }: { value: number | null | undefined }) {
  if (value == null || !Number.isFinite(value)) {
    return <span className={styles.muted}>—</span>;
  }
  const v = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div className={styles.scoreCell}>
      <span className={styles.num}>{v}</span>
      <div className={styles.scoreBar}>
        <div
          className={`${styles.scoreFill} ${v < 35 ? styles.scoreFillLow : ''}`}
          style={{ width: `${v}%` }}
        />
      </div>
    </div>
  );
}

export function DoelgroepenPage() {
  const { selectedProjectId, selectedProject, setSelectedProjectId } = useProjectContext();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'active' | 'archived'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'static' | 'dynamic'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('updated');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const listQ = useQuery({
    queryKey: ['audiences', selectedProjectId],
    enabled: Boolean(selectedProjectId),
    queryFn: () =>
      apiFetch<{ audiences: AudienceSummary[] }>(
        `/api/audiences?project_id=${selectedProjectId}`,
      ),
  });

  const audiences = listQ.data?.audiences ?? [];

  const summary = useMemo(() => {
    let contacts = 0;
    let campaigns = 0;
    let active = 0;
    let oppSum = 0;
    let oppN = 0;
    for (const a of audiences) {
      contacts += displayContactCount(a);
      campaigns += a.campaign_count ?? 0;
      if (a.status === 'active') active++;
      if (a.opportunity_score != null && Number.isFinite(a.opportunity_score)) {
        oppSum += a.opportunity_score;
        oppN++;
      }
    }
    return {
      total: audiences.length,
      contacts,
      campaigns,
      active,
      avgOpportunity: oppN ? Math.round(oppSum / oppN) : null,
    };
  }, [audiences]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = audiences.filter((a) => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (typeFilter !== 'all' && a.audience_type !== typeFilter) return false;
      if (!q) return true;
      return (
        a.name.toLowerCase().includes(q) ||
        (a.description?.toLowerCase().includes(q) ?? false) ||
        (a.parent_name?.toLowerCase().includes(q) ?? false)
      );
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    rows = [...rows].sort((a, b) => {
      switch (sortKey) {
        case 'name':
          return dir * a.name.localeCompare(b.name, 'nl');
        case 'contacts':
          return dir * (displayContactCount(a) - displayContactCount(b));
        case 'opportunity':
          return dir * ((a.opportunity_score ?? -1) - (b.opportunity_score ?? -1));
        case 'campaigns':
          return dir * (a.campaign_count - b.campaign_count);
        case 'updated':
        default:
          return dir * (Date.parse(a.updated_at) - Date.parse(b.updated_at));
      }
    });
    return rows;
  }, [audiences, search, statusFilter, typeFilter, sortKey, sortDir]);

  const createMut = useMutation({
    mutationFn: () =>
      apiFetch('/api/audiences', {
        method: 'POST',
        body: JSON.stringify({
          project_id: selectedProjectId,
          name: name.trim(),
          description: description.trim() || null,
          status: 'active',
          audience_type: 'static',
        }),
      }),
    onSuccess: () => {
      setName('');
      setDescription('');
      setCreateOpen(false);
      void qc.invalidateQueries({ queryKey: ['audiences'] });
    },
  });

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  }

  function sortIndicator(key: SortKey): string {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  }

  return (
    <ProjectGate pageTitle="Doelgroepen">
      <AudiencesFlagGate>
        {!selectedProjectId ? (
          <ProjectPickerEmpty
            title="Doelgroepen"
            description="Strategische doelgroepen met campagnelijst, scores en Brevo-sync. Kies eerst een werkruimte."
            cardHint="Doelgroepen & segmenten"
          />
        ) : listQ.isLoading ? (
          <LoadingState />
        ) : listQ.isError ? (
          <ErrorState message={(listQ.error as Error).message} />
        ) : (
          <div className={styles.page}>
            <ProjectWorkspaceHeader
              title="Doelgroepen"
              subtitle="Segmenten, bedrijven en campagnes op één plek — inclusief opportunity-scores en reply-KPI’s."
              projectName={selectedProject?.name ?? ''}
              onSwitchProject={() => setSelectedProjectId(null)}
            />

            <div className={styles.summaryGrid}>
              <div className={styles.summaryCard}>
                <p className={styles.summaryLabel}>Doelgroepen</p>
                <p className={styles.summaryValue}>{summary.total}</p>
                <p className={styles.summaryHint}>{summary.active} actief</p>
              </div>
              <div className={styles.summaryCard}>
                <p className={styles.summaryLabel}>Contacten (rollup)</p>
                <p className={styles.summaryValue}>{summary.contacts.toLocaleString('nl-NL')}</p>
                <p className={styles.summaryHint}>Inclusief sub-doelgroepen</p>
              </div>
              <div className={styles.summaryCard}>
                <p className={styles.summaryLabel}>Gekoppelde campagnes</p>
                <p className={styles.summaryValue}>{summary.campaigns}</p>
                <p className={styles.summaryHint}>Hub-campagnes met audience_id</p>
              </div>
              <div className={styles.summaryCard}>
                <p className={styles.summaryLabel}>Gem. opportunity</p>
                <p className={styles.summaryValue}>
                  {summary.avgOpportunity != null ? summary.avgOpportunity : '—'}
                </p>
                <p className={styles.summaryHint}>Laatste score-run</p>
              </div>
            </div>

            <div className={styles.toolbar}>
              <div className={styles.toolbarFilters}>
                <div className={styles.searchWrap}>
                  <TextInput
                    placeholder="Zoek op naam of omschrijving…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    aria-label="Zoeken"
                  />
                </div>
                <SelectInput
                  className={styles.select}
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as typeof statusFilter)
                  }
                  aria-label="Status filter"
                >
                  <option value="all">Alle statussen</option>
                  <option value="active">Actief</option>
                  <option value="draft">Concept</option>
                  <option value="archived">Gearchiveerd</option>
                </SelectInput>
                <SelectInput
                  className={styles.select}
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)}
                  aria-label="Type filter"
                >
                  <option value="all">Alle types</option>
                  <option value="static">Statisch</option>
                  <option value="dynamic">Dynamisch</option>
                </SelectInput>
              </div>
              <Button type="button" onClick={() => setCreateOpen((v) => !v)}>
                {createOpen ? 'Sluiten' : 'Nieuwe doelgroep'}
              </Button>
            </div>

            {createOpen ? (
              <form
                className={styles.createPanel}
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  createMut.mutate();
                }}
              >
                <h2 className={styles.panelTitle}>Doelgroep aanmaken</h2>
                <p className={styles.panelMeta}>
                  De campagnelijst wordt automatisch gekoppeld; contacten voeg je toe op de
                  detailpagina.
                </p>
                <div className={styles.createGrid}>
                  <Field label="Naam">
                    <TextInput value={name} onChange={(e) => setName(e.target.value)} required />
                  </Field>
                  <Field label="Omschrijving">
                    <TextArea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                    />
                  </Field>
                </div>
                <div className={styles.createActions}>
                  <Button type="submit" disabled={createMut.isPending || !name.trim()}>
                    Aanmaken
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>
                    Annuleren
                  </Button>
                </div>
              </form>
            ) : null}

            <section className={styles.panel}>
              <div className={styles.panelHead}>
                <div>
                  <h2 className={styles.panelTitle}>Overzicht</h2>
                  <p className={styles.panelMeta}>
                    {filtered.length} van {audiences.length} doelgroepen
                    {search.trim() ? ` · zoek: “${search.trim()}”` : ''}
                  </p>
                </div>
              </div>

              {audiences.length === 0 ? (
                <div className={styles.emptyPanel}>
                  <h3 className={styles.emptyTitle}>Nog geen doelgroepen</h3>
                  <p className={styles.emptyText}>
                    Maak een doelgroep aan, voeg contacten of bedrijven toe en koppel campagnes.
                    Dynamische doelgroepen kun je later verversen op regels.
                  </p>
                  <Button type="button" onClick={() => setCreateOpen(true)}>
                    Eerste doelgroep aanmaken
                  </Button>
                </div>
              ) : filtered.length === 0 ? (
                <div className={styles.emptyPanel}>
                  <h3 className={styles.emptyTitle}>Geen resultaten</h3>
                  <p className={styles.emptyText}>Pas filters of zoekterm aan.</p>
                </div>
              ) : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th
                          className={styles.sortable}
                          onClick={() => toggleSort('name')}
                        >
                          Doelgroep{sortIndicator('name')}
                        </th>
                        <th>Status</th>
                        <th>Type</th>
                        <th
                          className={styles.sortable}
                          onClick={() => toggleSort('contacts')}
                        >
                          Contacten{sortIndicator('contacts')}
                        </th>
                        <th>Bedrijven</th>
                        <th
                          className={styles.sortable}
                          onClick={() => toggleSort('campaigns')}
                        >
                          Campagnes{sortIndicator('campaigns')}
                        </th>
                        <th
                          className={styles.sortable}
                          onClick={() => toggleSort('opportunity')}
                        >
                          Opportunity{sortIndicator('opportunity')}
                        </th>
                        <th>Confidence</th>
                        <th>Response</th>
                        <th
                          className={styles.sortable}
                          onClick={() => toggleSort('updated')}
                        >
                          Bijgewerkt{sortIndicator('updated')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((a) => (
                        <tr key={a.id}>
                          <td>
                            <Link to={`/app/doelgroepen/${a.id}`} className={styles.rowLink}>
                              <span className={styles.nameCell}>{a.name}</span>
                              {a.parent_name ? (
                                <span className={styles.subName}>Sub · {a.parent_name}</span>
                              ) : (a.children_count ?? 0) > 0 ? (
                                <span className={styles.subName}>
                                  {a.children_count} sub-doelgroepen
                                </span>
                              ) : null}
                              {a.description ? (
                                <span className={styles.descClamp}>{a.description}</span>
                              ) : null}
                            </Link>
                          </td>
                          <td>
                            <span
                              className={`${styles.badge} ${statusBadgeClass(a.status)}`}
                            >
                              {AUDIENCE_STATUS_LABELS[
                                a.status as keyof typeof AUDIENCE_STATUS_LABELS
                              ] ?? a.status}
                            </span>
                          </td>
                          <td>
                            <span className={styles.typePill}>
                              {TYPE_LABELS[a.audience_type] ?? a.audience_type}
                              {a.audience_type === 'dynamic' && a.refresh_enabled ? ' · auto' : ''}
                            </span>
                          </td>
                          <td>
                            <span className={styles.num}>{displayContactCount(a)}</span>
                            {!a.parent_audience_id &&
                            (a.children_count ?? 0) > 0 &&
                            (a.rollup_lead_count ?? 0) !== a.lead_count ? (
                              <span className={styles.subName}>
                                direct {a.lead_count}
                              </span>
                            ) : null}
                          </td>
                          <td className={styles.num}>{a.account_count}</td>
                          <td className={styles.num}>{a.campaign_count}</td>
                          <td>
                            <ScoreMini value={a.opportunity_score} />
                          </td>
                          <td>
                            <ScoreMini value={a.confidence_score} />
                          </td>
                          <td>
                            {(a.kpi_enrollments ?? 0) > 0 ? (
                              <span className={styles.num}>
                                {formatAudienceReplyRatePct(a.kpi_reply_rate ?? undefined)}
                              </span>
                            ) : (
                              <span className={styles.muted}>—</span>
                            )}
                          </td>
                          <td className={styles.muted}>
                            {new Date(a.updated_at).toLocaleDateString('nl-NL', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </AudiencesFlagGate>
    </ProjectGate>
  );
}
